import { useState, useEffect, useRef, ReactNode } from 'react';
import Dropdown from './Dropdown';
import { Link, Button } from 'theme-ui';

const submenuHoverBackgroundColor = '#eee';
const isHamburger = window.innerWidth < 960

interface StyledButtonProps {
  dropdown: boolean
  setDropdown: Function
  isSubmenu: boolean
  children: ReactNode
}

function StyledButton({ dropdown, setDropdown, isSubmenu, children }: StyledButtonProps) {
  return (
    <Button
      aria-haspopup="menu"
      aria-expanded={dropdown ? 'true' : 'false'}
      onClick={() => setDropdown((prev: boolean) => !prev)}
      backgroundColor="transparent"
      color={isSubmenu ? '#3a3933' : '#a09e99'}
      sx={{
        display: 'flex',
        alignItems: 'center',
        fontSize: 'inherit',
        border: 'none',
        cursor: 'default',
        width: '100%',
        textAlign: 'left',
        p: 0,
        pb: isHamburger && '5px',
        ':hover': {
          color: isSubmenu ? undefined : '#fff',
          backgroundColor: isSubmenu ? submenuHoverBackgroundColor : 'transparent'
        }
      }}
    >
      {children}
    </Button>
  );
}

interface StyledLinkProps {
  href: string
  isSubmenu: boolean
  children: ReactNode
}

function StyledLink({ isSubmenu, href, children }: StyledLinkProps) {
  return (
    <Link
      href={href}
      color={isSubmenu ? '#a09e99' : '#3a3933'}
      sx={{
        color: isSubmenu ? '#3a3933' : '#a09e99',
        display: 'block',
        fontSize: 'inherit',
        textDecoration: 'none',
        textAlign: 'left',
        cursor: href ? 'pointer' : 'default',
        p: [`5px ${isSubmenu ? '1rem' : 0}`, null, null, '5px 0.6rem'],
        ':hover': {
          color: isSubmenu ? 'initial' : '#fff',
          backgroundColor: isSubmenu ? submenuHoverBackgroundColor : undefined,
          textDecoration: 'none'
        }
      }}
    >
      {children}
    </Link>
  );
}

function DisclosureIndicator() {
  return <span
    sx={{
      '::after': {
        content: '""',
        display: 'inline-block',
        marginRight: '0.28em',
        verticalAlign: 'middle',
        borderTop: '4px solid',
        borderRight: '4px solid transparent',
        borderLeft: '4px solid transparent'
      }
    }}
  />
}

export default function MenuItems({ items, depthLevel }) {
  const [dropdown, setDropdown] = useState<boolean>(false);

  const ref = useRef<any>();

  const isSubmenu = depthLevel > 0;

  useEffect(() => {
    const handler = (event) => {
      if (
        dropdown &&
        ref.current &&
        !ref.current.contains(event.target)
      ) {
        setDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      // Cleanup the event listener
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [dropdown]);

  const onMouseEnter = () => {
    isHamburger && setDropdown(true);
  };

  const onMouseLeave = () => {
    isHamburger && setDropdown(false);
  };

  const closeDropdown = () => {
    dropdown && setDropdown(false);
  };

  return (
    <li
      ref={ref}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={closeDropdown}
      sx={{
        position: 'relative',
      }}
    >
      {items.submenu ? (
        <>
          <StyledButton
            dropdown={dropdown}
            setDropdown={setDropdown}
            isSubmenu={isSubmenu}
          >
            <StyledLink
              href={items.url}
              isSubmenu={isSubmenu}
            >
              <DisclosureIndicator />
              {items.title}
            </StyledLink>
          </StyledButton>
          <Dropdown
            depthLevel={depthLevel}
            submenus={items.submenu}
            dropdown={dropdown}
          />
        </>
      ) : (
        <StyledLink
          href={items.url}
          isSubmenu={isSubmenu}
        >
          {items.title}
        </StyledLink>
      )}
    </li>
  );
}
